import React from "react"
import { useSelector } from "react-redux"

import Images from "assets/images"
import { PROVEN_NO, PROVEN_IN_USE, PRIOR_USE } from "const"
import {
    SilDevice,
    SilProjectDevice,
    SilDeviceNested,
    SilProjectDeviceNested,
    SilSubsystemBlock,
} from "server/models"
import { deepCopy } from "client/utils"
import {
    StoreType,
    SilDeviceType,
} from "client/store"
import {
    Logo,
    DateFormatter,
    TableColumn,
    TablePopoverData,
    TablePopover,
} from "client/components"

import { getSilMode } from "client/pages/sil/formulas"

const Columns = (showModelColumn = false) => {
    const modelTarget = useSelector((state: StoreType) => state.silModelDevices.modelTarget)
    const onSelect = useSelector((state: StoreType) => state.silModelDevices.onSelect)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const onSelectDevice = (device: SilDeviceType) => {
        const accountDevice = device as SilDevice
        const projectDevice = device as SilProjectDevice

        // verify that "deviceId" and "qidProductId" are not "undefined" and equal to "null"
        const projectDeviceId = projectDevice.deviceId === null && projectDevice.qidProductId === null ? projectDevice.id : null
        const deviceId = accountDevice.accountId ? accountDevice.id : projectDevice.deviceId

        if (modelTarget === "subsystem") {
            const nestedDevice: SilDeviceNested = {
                id: null,
                parentDeviceId: null,
                deviceId,

                qidProductId: projectDevice.qidProductId || null,
                qidModelId: projectDevice.qidModelId || null,
                qidSafetyFunctionId: projectDevice.qidSafetyFunctionId || null,
                qidOptionId: projectDevice.qidOptionId || null,
                qidDatapointId: projectDevice.qidDatapointId || null,

                qidModelName: projectDevice.qidModelName || null,
                qidSafetyFunctionName: projectDevice.qidSafetyFunctionName || null,
                qidOptionName: projectDevice.qidOptionName || null,
                qidDatapointDate: projectDevice.qidDatapointDate || null,
                qidMethod: projectDevice.qidMethod || null,

                manufacturer: device.manufacturer,
                name: device.name,
                logoId: device.logoId,
                logo: device.logo,
                type: device.type,
                mode: getSilMode(device),
                logicSolver: device.logicSolver,
                provenInUse: device.provenInUse,

                sd: device.sd,
                su: device.su,
                dd: device.dd,
                du: device.du,

                order: null,
            }

            const nestedProjectDevice: SilProjectDeviceNested = {
                ...nestedDevice,
                projectDeviceId,
            }

            onSelect(project.id ? nestedProjectDevice : nestedDevice)
        }

        if (modelTarget === "safetyFunction") {
            const subsystemBlock: SilSubsystemBlock = {
                id: null,
                subsystemId: null,
                deviceId,
                projectDeviceId,

                qidProductId: projectDevice.qidProductId || null,
                qidModelId: projectDevice.qidModelId || null,
                qidSafetyFunctionId: projectDevice.qidSafetyFunctionId || null,
                qidOptionId: projectDevice.qidOptionId || null,
                qidDatapointId: projectDevice.qidDatapointId || null,
                qidMethod: projectDevice.qidMethod || null,

                order: null,
                projectDevice: {
                    id: projectDeviceId,
                    projectId: project.id,
                    deviceId,

                    qidProductId: projectDevice.qidProductId || null,
                    qidModelId: projectDevice.qidModelId || null,
                    qidSafetyFunctionId: projectDevice.qidSafetyFunctionId || null,
                    qidOptionId: projectDevice.qidOptionId || null,
                    qidDatapointId: projectDevice.qidDatapointId || null,

                    qidModelName: projectDevice.qidModelName || null,
                    qidSafetyFunctionName: projectDevice.qidSafetyFunctionName || null,
                    qidOptionName: projectDevice.qidOptionName || null,
                    qidDatapointDate: projectDevice.qidDatapointDate || null,
                    qidMethod: projectDevice.qidMethod || null,

                    manufacturer: device.manufacturer,
                    name: device.name,
                    logoId: device.logoId,
                    logo: device.logo,
                    description: device.description,
                    reference: device.reference,
                    nested: device.nested,
                    type: device.type,
                    lowDemandMode: device.lowDemandMode,
                    highDemandMode: device.highDemandMode,
                    continuousMode: device.continuousMode,
                    logicSolver: device.logicSolver,
                    provenInUse: device.provenInUse,

                    failureRate: device.failureRate,
                    sff: device.sff,
                    dc: device.dc,
                    pfd: device.pfd,
                    pfh: device.pfh,
                    sil: device.sil,

                    sd: device.sd,
                    su: device.su,
                    dd: device.dd,
                    du: device.du,

                    status: device.status,
                    copy: 0,
                    updated: false,

                    nestedDevices: deepCopy(device.nestedDevices),
                },
            }

            onSelect(subsystemBlock)
        }
    }

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
            onClick: onSelectDevice,
        },
        {
            field: "logo",
            header: lang.logo,
            centerAlign: true,
            formatter: (device: SilDeviceType) => (
                <Logo table logo={device.logo || Images.silDeviceLogo} />
            ),
            onClick: onSelectDevice,
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            onClick: onSelectDevice,
        },
    ]

    if (showModelColumn) {
        columns.push({
            field: "qidModelName",
            header: lang.model,
            sortable: false,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
        })
    }

    columns.push(
        {
            field: "manufacturer",
            header: lang.manufacturer,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            onClick: onSelectDevice,
        },
        {
            field: "type",
            header: lang.type,
            sortable: true,
            cellMinWidth: "100px",
            onClick: onSelectDevice,
        },
        {
            field: "mode",
            header: lang.mode,
            sortable: false,
            cellMinWidth: "150px",
            formatter: (device: SilDeviceType) => {
                const dataList: TablePopoverData[] = []

                if (device.lowDemandMode) {
                    dataList.push({
                        id: `${device.id}lowDemandMode`,
                        title: lang.modeLowDemand,
                    })
                }
                if (device.highDemandMode) {
                    dataList.push({
                        id: `${device.id}highDemandMode`,
                        title: lang.modeHighDemand,
                    })
                }
                if (device.continuousMode) {
                    dataList.push({
                        id: `${device.id}continuousMode`,
                        title: lang.modeContinuous,
                    })
                }

                return (
                    <TablePopover
                        id={`${device.id}modeList`}
                        dataList={dataList}
                        placement="right"
                        title={lang.modes}
                        editAccess={false}
                    />
                )
            },
            onClick: onSelectDevice,
        },
        {
            field: "logicSolver",
            header: lang.logicSolver,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (device: SilDeviceType) => (device.logicSolver ? lang.yes : lang.no),
            onClick: onSelectDevice,
        },
        {
            field: "provenInUse",
            header: lang.provenReliabilityData,
            sortable: true,
            cellMinWidth: "200px",
            formatter: (device: SilDeviceType) => {
                if (device.provenInUse === PROVEN_NO) return lang.no
                if (device.provenInUse === PROVEN_IN_USE) return lang.provenInUse
                if (device.provenInUse === PRIOR_USE) return lang.priorUse
                return null
            },
            onClick: onSelectDevice,
        },
        {
            field: "status",
            header: lang.status,
            centerAlign: true,
            sortable: true,
            onClick: onSelectDevice,
        },
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            formatter: (device: SilDeviceType) => <DateFormatter date={device.createdAt} />,
            onClick: onSelectDevice,
        },
    )

    return columns
}
export default Columns
